import { http } from "../../../shared/api/http"

/* ── Orders ── */

export type OrderStatus = "pending" | "paid" | "canceled" | "expired" | "refunded"
export type OrderTargetType = "course" | "subscription_plan"

export interface OrderItemLine {
  target_type: OrderTargetType
  target_id: string
  title: string
  unit_amount: number
  quantity: number
  subtotal: number
}

export interface OrderItem {
  id: string
  user_id: string
  items: OrderItemLine[]
  currency: string
  total_amount: number
  status: OrderStatus
  created_at: string
}

export type OrderDetail = OrderItem

export interface ListOrdersResponse {
  items: OrderItem[]
  total: number
}

export type ListOrdersFilters = {
  limit?: number
  offset?: number
  status?: OrderStatus | ""
  user_id?: string
}

export const listOrdersApi = (filters: ListOrdersFilters) =>
  http.get<ListOrdersResponse>("/api/admin/orders", { params: { ...filters, status: filters.status || undefined } })

export const getOrderApi = (id: string) =>
  http.get<OrderDetail>(`/api/admin/orders/${id}`)

/* ── Payments ── */

export type PaymentStatus = "pending" | "completed" | "failed"

export interface PaymentItem {
  id: string
  order_id: string
  amount: number
  currency: string
  status: PaymentStatus
  provider: string
  created_at: string
}

export interface ListPaymentsResponse {
  items: PaymentItem[]
  total: number
}

export type ListPaymentsFilters = {
  limit?: number
  offset?: number
  status?: PaymentStatus | ""
  order_id?: string
}

export const listPaymentsApi = (filters: ListPaymentsFilters) =>
  http.get<ListPaymentsResponse>("/api/admin/payments", { params: { ...filters, status: filters.status || undefined } })

export const checkPaymentStatusApi = (id: string) =>
  http.post<PaymentItem>(`/api/admin/payments/${id}/check-status`)

/* ── Subscriptions ── */

export type SubscriptionStatus = "active" | "expired" | "canceled"
export type SubscriptionTargetType = "china_access" | "job_access"

export interface SubscriptionItem {
  id: string
  user_id: string
  plan_id: string
  target_type: SubscriptionTargetType
  status: SubscriptionStatus
  current_period_start: string
  current_period_end: string
}

export interface ListSubscriptionsResponse {
  items: SubscriptionItem[]
  total: number
}

export type ListSubscriptionsFilters = {
  limit?: number
  offset?: number
  status?: SubscriptionStatus | ""
  target_type?: SubscriptionTargetType | ""
  user_id?: string
}

export const listSubscriptionsApi = (filters: ListSubscriptionsFilters) =>
  http.get<ListSubscriptionsResponse>("/api/admin/subscriptions", {
    params: { ...filters, status: filters.status || undefined, target_type: filters.target_type || undefined },
  })

/* ── Subscription Plans ── */

export type Currency = "KGS"
export type BillingPeriod = "monthly"

export interface SubscriptionPlan {
  id: string
  codename: string
  title: string
  target_type: SubscriptionTargetType
  amount: number
  currency: Currency
  billing_period: BillingPeriod
  is_active: boolean
}

export interface ListSubscriptionPlansResponse {
  items: SubscriptionPlan[]
  total: number
}

export type ListSubscriptionPlansFilters = {
  limit?: number
  offset?: number
  is_active?: boolean
  target_type?: SubscriptionTargetType | ""
}

export const listSubscriptionPlansApi = (filters: ListSubscriptionPlansFilters) =>
  http.get<ListSubscriptionPlansResponse>("/api/admin/subscription-plans", {
    params: { ...filters, target_type: filters.target_type || undefined },
  })

export type CreateSubscriptionPlanPayload = {
  codename: string
  title: string
  target_type: SubscriptionTargetType
  amount: number
  currency: Currency
  billing_period: BillingPeriod
  is_active: boolean
}

export const createSubscriptionPlanApi = (payload: CreateSubscriptionPlanPayload) =>
  http.post<SubscriptionPlan>("/api/admin/subscription-plans", payload)

export type UpdateSubscriptionPlanPayload = Partial<CreateSubscriptionPlanPayload>

export const updateSubscriptionPlanApi = (planId: string, payload: UpdateSubscriptionPlanPayload) =>
  http.patch<SubscriptionPlan>(`/api/admin/subscription-plans/${planId}`, payload)

/* ── China access config ── */

export interface ChinaAccessConfig {
  preview_detail_limit: number
  hide_contacts_without_subscription: boolean
  hide_reviews_without_subscription: boolean
}

export const getChinaAccessConfigApi = () =>
  http.get<ChinaAccessConfig>("/api/admin/china/access-config")

export const updateChinaAccessConfigApi = (payload: Partial<ChinaAccessConfig>) =>
  http.patch<ChinaAccessConfig>("/api/admin/china/access-config", payload)

/* ── Usage config (job_swipe, vacancy_applicant_contacts) ── */

export type UsageConfigKey = "job_swipe" | "vacancy_applicant_contacts"

export interface UsageConfig {
  limit: number
}

export const getUsageConfigApi = (key: UsageConfigKey) =>
  http.get<UsageConfig>(`/api/admin/usage-config/${key}`)

export const updateUsageConfigApi = (key: UsageConfigKey, limit: number) =>
  http.patch<UsageConfig>(`/api/admin/usage-config/${key}`, { limit })

/* ── IAP course price tiers ── */

export type PriceTierOption = {
  tier: number
  price_usd_cents: number
  apple_product_id: string
  google_product_id: string
}

export type PriceTiersResponse = {
  items: PriceTierOption[]
}

/** Allowed price values for a paid/mixed course — use for the price dropdown, not a free-text amount. */
export const listCoursePriceTiersApi = () =>
  http.get<PriceTiersResponse>("/api/admin/courses/price-tiers")

export type IapTier = {
  tier: number
  price_usd_cents: number
  apple_product_id: string
  google_product_id: string
  is_active: boolean
}

export type IapTiersResponse = {
  items: IapTier[]
}

export const listIapTiersApi = (includeInactive?: boolean) =>
  http.get<IapTiersResponse>("/api/admin/iap-tiers", {
    params: includeInactive ? { include_inactive: true } : undefined,
  })

export type CreateIapTierPayload = {
  tier: number
  price_usd_cents: number
  apple_product_id: string
  google_product_id: string
}

export const createIapTierApi = (payload: CreateIapTierPayload) =>
  http.post<IapTier>("/api/admin/iap-tiers", payload)

export type UpdateIapTierPayload = Partial<{
  price_usd_cents: number
  apple_product_id: string
  google_product_id: string
  is_active: boolean
}>

export const updateIapTierApi = (tier: number, payload: UpdateIapTierPayload) =>
  http.patch<IapTier>(`/api/admin/iap-tiers/${tier}`, payload)